/**
 * DemoModeToggleButton — a compact top-right header control that turns Demo
 * (Bemutató) mode on and off during live customer presentations.
 *
 * It is SELF-GATING: it renders `null` unless the current operator is allowed to
 * run demos — i.e. a `system:admin` (see
 * {@link canUseDemoMode}). It is baked directly into the
 * shared header primitives (GradientHero, PremiumHeader) so it appears in the
 * top-right on the surfaces sales reps and managers actually operate from, with
 * no per-screen wiring — mirroring the admin panel's top-right Demo toggle.
 *
 * Turning demo mode on immediately unlocks EVERY module / menu / page (see the
 * `isDemoMode()` guards in `managerRoles`/`managementAccess`) and swaps all real
 * PII for synthetic demo data. The eye icon reflects the current state (open =
 * on/visible-as-demo, orange brand tint) and the persistent {@link DemoModeBadge}
 * doubles as the always-on indicator, so no confirm dialog is needed (matching
 * the existing Settings switch, which toggles without a prompt).
 */

import React, {useEffect, useState} from 'react';
import {StyleSheet, TouchableOpacity} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import {isDemoMode, setDemoMode, subscribeDemoMode} from '../demo';
import {getUserInfo} from '../helpers/storage';
import {canUseDemoMode} from '../helpers/managerRoles';

/**
 * @param {object} props
 * @param {number} [props.size=20] icon size.
 * @param {string} [props.color] icon tint while demo mode is OFF (defaults to a
 *   muted slate; pass '#FFFFFF' on dark/gradient headers).
 * @param {object} [props.style] extra style merged onto the touch target.
 * @returns {React.ReactElement|null} the toggle, or null when the user may not run demos.
 */
const DemoModeToggleButton = ({size = 20, color, style}) => {
    const [allowed, setAllowed] = useState(false);
    const [on, setOn] = useState(() => isDemoMode());

    // Reflect demo-mode changes made anywhere (this button, the Settings switch,
    // logout resetting it) so the icon/tint stays in sync.
    useEffect(() => subscribeDemoMode(setOn), []);

    // Resolve eligibility from the stored operator. getUserInfo() preserves roles
    // even while demo mode redacts PII, so the button stays visible once demo is
    // on. Re-evaluates whenever this header mounts (i.e. on navigation/login).
    useEffect(() => {
        let alive = true;

        (async () => {
            try {
                const user = await getUserInfo();
                if (alive) {
                    setAllowed(canUseDemoMode(user));
                }
            } catch (_) {
                // no stored user yet / storage unavailable — stay hidden
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    if (!allowed) {
        return null;
    }

    const tint = on ? '#F97316' : (color || '#94A3B8');

    return (
        <TouchableOpacity
            onPress={() => setDemoMode(!isDemoMode())}
            activeOpacity={0.8}
            hitSlop={10}
            accessibilityRole="switch"
            accessibilityState={{ checked: on }}
            accessibilityLabel="demo-mode-toggle"
            style={[styles.btn, on ? styles.btnOn : null, style]}
        >
            <Icon name={on ? 'eye' : 'eye-off'} size={size} color={tint} />
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    btn: {
        width: 40,
        height: 40,
        borderRadius: 20,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(148, 163, 184, 0.14)',
    },
    btnOn: {
        backgroundColor: 'rgba(249, 115, 22, 0.18)',
    },
});

export default DemoModeToggleButton;
